/**
 * Sentry event sanitization — single source of truth for PII redaction
 * before any event leaves the browser. Lives in its own module so it can
 * be exhaustively unit-tested without booting the Sentry SDK.
 *
 * Two complementary passes:
 *   1. Key-based — recursive walk over breadcrumbs.data, extra, contexts,
 *      tags, user, request.{data,headers}. Any object key whose lowercased
 *      name contains one of `SENSITIVE_KEY_SUBSTRINGS` is replaced with
 *      `[REDACTED]`, regardless of the value.
 *   2. Pattern-based — every string value (including event.message and
 *      event.exception.values[].value) is run through `STRING_PATTERNS`.
 *      Catches PII interpolated into Error messages where key-based
 *      scrubbing cannot reach (e.g. `new Error("Invalid CURP: ABC..."")`).
 *
 * Conservative on purpose: only patterns with distinctive shapes that
 * would be unusual in legitimate error text. False positives are
 * preferable to PII leaks — we accept that "LOG241010ERR" could match
 * the RFC shape and get redacted.
 */

import type { Event as SentryEvent } from '@sentry/react';

export const REDACTED = '[REDACTED]';

/**
 * Case-insensitive substrings. Any object key whose lowercased name
 * contains one of these is wholesale redacted. Keep alphabetical-ish for
 * easy auditing; intentional duplicates between snake/camel conventions
 * (`session_id` + `sessionid`, `birth_date` + `birthdate`, etc.) so we
 * catch both shapes the backend may emit.
 */
export const SENSITIVE_KEY_SUBSTRINGS: readonly string[] = [
  'password',
  'token',
  'authorization',
  'bearer',
  'clabe',
  'curp',
  'rfc',
  'cardnumber',
  'cvv',
  'pin',
  'email',
  'phone',
  'birth_date',
  'birthdate',
  'address',
  'street',
  'fullname',
  'full_name',
  'extracted_data',
  'documentextraction',
  'document_extraction',
  'onboarding_token',
  'session_id',
  'sessionid',
  'jti',
];

/**
 * Free-form string patterns. Run against every string we walk over,
 * including `event.message` and `event.exception.values[].value`.
 *
 *   - JWT: three base64url segments separated by dots, prefix `eyJ`
 *   - CURP: 18-char Mexican identity code (length + positional alphabet)
 *   - RFC:  13 chars (persona física) or 12 chars (persona moral),
 *           leading 3-4 letters may include `Ñ` or `&`
 */
export const STRING_PATTERNS: readonly RegExp[] = [
  /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g,
  /\b[A-Z][AEIOUX][A-Z]{2}\d{6}[HM][A-Z]{5}[A-Z0-9]\d\b/g,
  /\b[A-Z&Ñ]{3,4}\d{6}[A-Z0-9]{3}\b/g,
];

const MAX_SCRUB_DEPTH = 6;

export function scrubString(value: string): string {
  return STRING_PATTERNS.reduce((acc, re) => acc.replace(re, REDACTED), value);
}

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return SENSITIVE_KEY_SUBSTRINGS.some(s => lower.includes(s));
}

/**
 * Recursively redact PII from arbitrary structured data. Object keys are
 * matched against `SENSITIVE_KEY_SUBSTRINGS`; string values pass through
 * `scrubString`. Arrays are walked element-wise. Depth is bounded at
 * `MAX_SCRUB_DEPTH` to defend against pathological / cyclic input — values
 * past that depth are returned untouched.
 */
export function scrubDeep(value: unknown, depth = 0): unknown {
  if (depth > MAX_SCRUB_DEPTH || value === null || value === undefined) return value;
  if (typeof value === 'string') return scrubString(value);
  if (Array.isArray(value)) return value.map(v => scrubDeep(v, depth + 1));
  if (typeof value !== 'object') return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = isSensitiveKey(k) ? REDACTED : scrubDeep(v, depth + 1);
  }
  return out;
}

/**
 * Run both passes over a Sentry event in-place-style (returns a new object
 * to avoid mutating the caller's reference). Touches every surface known
 * to carry PII:
 *
 *   breadcrumbs.data · extra · contexts · tags · user
 *   request.data · request.headers · message · exception.values[].value
 */
export function sanitizeSentryEvent(event: SentryEvent): SentryEvent {
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(b => ({
      ...b,
      data: b.data ? (scrubDeep(b.data) as typeof b.data) : b.data,
    }));
  }
  if (event.extra) event.extra = scrubDeep(event.extra) as typeof event.extra;
  if (event.contexts) event.contexts = scrubDeep(event.contexts) as typeof event.contexts;
  if (event.tags) event.tags = scrubDeep(event.tags) as typeof event.tags;
  // setUser({ id, email }) lands here; id is an opaque diagnostic value,
  // email matches the substring list and gets redacted. Sentry also
  // auto-populates ip_address on this object, which the same pass covers.
  if (event.user) event.user = scrubDeep(event.user) as typeof event.user;
  if (event.request?.data) {
    event.request = { ...event.request, data: scrubDeep(event.request.data) };
  }
  if (event.request?.headers) {
    event.request = {
      ...event.request,
      headers: scrubDeep(event.request.headers) as Record<string, string>,
    };
  }
  // Free-form text surfaces — pattern scrub catches PII interpolated into
  // Error messages or stack-line contexts that the key-based pass cannot.
  if (typeof event.message === 'string') event.message = scrubString(event.message);
  if (event.exception?.values) {
    event.exception = {
      ...event.exception,
      values: event.exception.values.map(v => ({
        ...v,
        value: typeof v.value === 'string' ? scrubString(v.value) : v.value,
      })),
    };
  }

  return event;
}
